import { supabase } from "@/integrations/supabase/client";

const db = supabase as any;

export type LaunchStatus = "em_lancamento" | "em_obras" | "pronto_para_morar" | "consultar";
export type UnitStatus = "disponivel" | "reservada" | "vendida";

export interface Developer {
  id: string;
  nome: string;
  slug: string;
  logo_url: string | null;
  cor_marca: string | null;
  segmento: string | null;
  descricao: string | null;
  ativa: boolean;
}

export interface LaunchUnit {
  id: string;
  bloco: string | null;
  unidade: string;
  tipologia: string;
  quartos: number | null;
  area_m2: number;
  com_vaga: boolean;
  com_varanda: boolean;
  pne: boolean;
  observacao: string | null;
  valor_tabela: number;
  valor_caixa: number | null;
  condicao_desconto: string | null;
  status: UnitStatus;
  atualizado_em: string | null;
  ordem: number;
}

export interface LaunchChild {
  id: string;
  titulo?: string;
  nome?: string;
  nome_local?: string;
  pergunta?: string;
  resposta?: string;
  descricao?: string | null;
  imagem_url?: string | null;
  tipologia?: string | null;
  area_privativa?: number | null;
  icone?: string | null;
  minutos?: number;
  destaque?: boolean;
  ordem: number;
}

export interface Launch {
  id: string;
  name: string;
  slug: string | null;
  neighborhood: string;
  address: string | null;
  typologies: string[] | null;
  area_min: number | null;
  area_max: number | null;
  price_min: number | null;
  price_max: number | null;
  down_payment: string | null;
  delivery_date: string | null;
  description: string | null;
  profitability_study: string | null;
  developer: string | null;
  images: string[] | null;
  is_active: boolean | null;
  is_featured: boolean | null;
  created_at: string;
  updated_at: string;
  construtora_id: string | null;
  status_obra: LaunchStatus;
  cidade: string;
  uf: string;
  zona: string | null;
  latitude: number | null;
  longitude: number | null;
  headline: string | null;
  descricao_curta: string | null;
  descricao_longa: string | null;
  quartos_min: number | null;
  quartos_max: number | null;
  vagas_opcional: boolean;
  varanda_opcional: boolean;
  aceita_fgts: boolean;
  programa_habitacional: string | null;
  preco_a_partir: number | null;
  preco_a_partir_manual: number | null;
  hero_image_url: string | null;
  video_url: string | null;
  texto_legal: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  destaque: boolean;
  publicado: boolean;
  ordem: number;
  promocao_texto: string | null;
  nota_admin: string | null;
  construtora?: Developer | null;
  unidades?: LaunchUnit[];
  plantas?: LaunchChild[];
  galeria?: LaunchChild[];
  lazer?: LaunchChild[];
  diferenciais?: LaunchChild[];
  localizacao?: LaunchChild[];
  faqs?: LaunchChild[];
}

const launchSelect = `*, construtora:construtoras(*), unidades:empreendimento_unidades(*), plantas:empreendimento_plantas(*), galeria:empreendimento_galeria(*), lazer:empreendimento_lazer(*), diferenciais:empreendimento_diferenciais(*), localizacao:empreendimento_localizacao(*), faqs:empreendimento_faq(*)`;

const normalizeLaunch = (launch: Launch): Launch => ({
  ...launch,
  unidades: [...(launch.unidades || [])].sort((a, b) => a.ordem - b.ordem || a.valor_tabela - b.valor_tabela),
  plantas: [...(launch.plantas || [])].sort((a, b) => a.ordem - b.ordem),
  galeria: [...(launch.galeria || [])].sort((a, b) => a.ordem - b.ordem),
  lazer: [...(launch.lazer || [])].sort((a, b) => a.ordem - b.ordem),
  diferenciais: [...(launch.diferenciais || [])].sort((a, b) => a.ordem - b.ordem),
  localizacao: [...(launch.localizacao || [])].sort((a, b) => a.ordem - b.ordem),
  faqs: [...(launch.faqs || [])].sort((a, b) => a.ordem - b.ordem),
});

export async function fetchLaunches() {
  const { data, error } = await db.from("launches").select(launchSelect).eq("publicado", true).order("destaque", { ascending: false }).order("ordem");
  if (error) throw error;
  return (data as Launch[]).map(normalizeLaunch);
}

export async function fetchAllLaunchesAdmin() {
  const { data, error } = await db.from("launches").select(launchSelect).order("created_at", { ascending: false });
  if (error) throw error;
  return (data as Launch[]).map(normalizeLaunch);
}

export async function fetchDevelopers() {
  const { data, error } = await db.from("construtoras").select("*").order("ordem").order("nome");
  if (error) throw error;
  return data as Developer[];
}

export async function fetchLaunchById(id: string) {
  const { data, error } = await db.from("launches").select(launchSelect).eq("id", id).single();
  if (error) throw error;
  return normalizeLaunch(data as Launch);
}

export async function fetchLaunchBySlug(slug: string, includeDraft = false) {
  let query = db.from("launches").select(launchSelect).eq("slug", slug);
  if (!includeDraft) query = query.eq("publicado", true);
  const { data, error } = await query.single();
  if (error) throw error;
  return normalizeLaunch(data as Launch);
}

export function launchPath(launch: { slug?: string | null; id: string }): string {
  return `/lancamentos/${launch.slug || launch.id}`;
}

export async function fetchFeaturedLaunches() {
  const { data, error } = await db.from("launches").select("*").eq("publicado", true).eq("destaque", true).order("ordem").limit(6);
  if (error) throw error;
  return data as Launch[];
}

export async function submitLaunchLead(lead: {
  empreendimento_id: string;
  unidade_id?: string | null;
  nome: string;
  whatsapp: string;
  email?: string;
  interesse?: string;
  consentimento_lgpd: boolean;
  website?: string;
}) {
  if (lead.website) return;
  const params = new URLSearchParams(window.location.search);
  const { error } = await db.from("leads").insert({
    empreendimento_id: lead.empreendimento_id,
    unidade_id: lead.unidade_id || null,
    nome: lead.nome.trim(),
    whatsapp: lead.whatsapp.replace(/[^0-9]/g, ""),
    email: lead.email?.trim() || null,
    interesse: lead.interesse?.trim() || null,
    consentimento_lgpd: lead.consentimento_lgpd,
    origem: window.location.pathname,
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
  });
  if (error) throw error;
}